import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { T, F } from "@/lib/bricks";
import BricksLoader from "@/components/BricksLoader";
import { openPdf, PdfPages, initialsOf, useContainerWidth } from "@/components/esign/pdfPages";

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year:'numeric', month:'long', day:'numeric' });
}

export default function SigningPage() {
  const { token } = useParams();
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declined, setDeclined] = useState(false);
  const [busyDecline, setBusyDecline] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [docRef, docWidth] = useContainerWidth(528);
  const sigInputRef = useRef(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    fetch(`${BASE}/api/esign/sign/${token}`)
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          if (r.status === 410 && typeof body.detail === 'string') throw new Error(body.detail);
          throw new Error(r.status === 404 ? 'This signing link is invalid or has expired.' : 'Failed to load document.');
        }
        return r.json();
      })
      .then(d => {
        setEnvelope(d);
        if (d.recipient_status === 'signed') setSigned(true);
        setTypedSignature(d.recipient_name || '');
        const initialValues = {};
        (d.fields || []).forEach(f => {
          if (f.type === 'date') initialValues[f.id] = today;
        });
        setFieldValues(initialValues);
        setLoading(false);
      })
      .catch(err => { setError(err.message || 'Failed to load signing document.'); setLoading(false); });
  }, [token]); // eslint-disable-line

  const hasPlaced = !!(envelope?.document_url && (envelope?.fields || []).some(f => f.placed));
  useEffect(() => {
    if (!hasPlaced) return undefined;
    let live = true;
    openPdf(envelope.document_url).then(d => { if (live) setPdf(d); }).catch(() => { if (live) setPdfFailed(true); });
    return () => { live = false; };
  }, [hasPlaced, envelope?.document_url]); // eslint-disable-line

  // Fields drawn on the document vs. listed as a checklist (no PDF, older envelopes, or PDF failed to load).
  const onDoc = hasPlaced && !pdfFailed;
  const placedFields = onDoc ? (envelope?.fields || []).filter(f => f.placed) : [];
  const listFields = (envelope?.fields || []).filter(f => !(onDoc && f.placed));
  const blankRequired = (envelope?.fields || []).filter(f => f.type === 'text' && f.required !== false && !String(fieldValues[f.id] || '').trim());

  const handleSign = async () => {
    if (!agreed || !typedSignature.trim()) return;
    setSigning(true);
    try {
      const res = await fetch(`${BASE}/api/esign/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typed_signature: typedSignature.trim(), field_values: fieldValues }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Failed to submit signature. Please try again.');
      }
      setSigned(true);
    } catch (err) {
      setError(err.message || 'Failed to submit signature. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = async () => {
    setBusyDecline(true);
    try {
      const res = await fetch(`${BASE}/api/esign/sign/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason.trim() || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not decline. Please try again.');
      }
      setDeclined(true);
    } catch (err) {
      setError(err.message || 'Could not decline. Please try again.');
    } finally {
      setBusyDecline(false);
    }
  };

  // Minimal full-page layout (no DashboardLayout — public page)
  const pageStyle = {
    minHeight:'100vh', background:'#F4F1EB', display:'flex', alignItems:'center', justifyContent:'center',
    padding:'40px 16px', fontFamily:F.display,
  };
  const cardStyle = {
    maxWidth: onDoc ? 820 : 600, width:'100%',
  };

  if (loading) return (
    <div style={pageStyle}>
      <div style={{ textAlign:'center' }}>
        <BricksLoader variant="assemble" size={40} color={T.brick} style={{ display: 'block', margin: '0 auto' }} />
        <div style={{ marginTop:16, fontSize:14, color:'#6B6054' }}>Loading document…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, padding:48, textAlign:'center' }}>
        <div style={{ fontSize:20, fontWeight:500, color:'#14110E', letterSpacing:-0.5 }}>Link unavailable</div>
        <div style={{ fontSize:14, color:'#6B6054', marginTop:8 }}>{error}</div>
        <div style={{ fontSize:12, color:'#A39684', marginTop:24 }}>If this looks wrong, contact the sender.</div>
      </div>
    </div>
  );

  if (declined) return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, padding:48, textAlign:'center' }}>
        <div style={{ fontSize:22, fontWeight:500, color:'#14110E', letterSpacing:-0.6 }}>You declined to sign</div>
        <div style={{ fontSize:14, color:'#6B6054', marginTop:10, lineHeight:1.6 }}>
          {envelope?.title} is now closed and nobody else will be asked to sign it. The sender can see that you declined{declineReason.trim() ? ' and your reason' : ''}.
        </div>
      </div>
    </div>
  );

  if (signed) return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, padding:48, textAlign:'center' }}>
        <div style={{ display:'flex', justifyContent:'center', marginBottom:16 }}>
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#14110E" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12l5 5L20 6"/>
          </svg>
        </div>
        <div style={{ fontSize:24, fontWeight:500, color:'#14110E', letterSpacing:-0.8 }}>Document signed</div>
        <div style={{ fontSize:14, color:'#6B6054', marginTop:10 }}>Thanks, {envelope?.recipient_name}. Your signature is recorded.</div>
        <div style={{ marginTop:24, padding:'16px 0 0', borderTop:'1px solid #14111018', textAlign:'left' }}>
          <div style={{ fontSize:12, color:'#A39684' }}>Confirmation</div>
          <div style={{ fontSize:14, color:'#3B332A', marginTop:8 }}>{envelope?.title}</div>
          <div style={{ fontSize:12, color:'#6B6054', marginTop:4 }}>Signed on {fmtDate(new Date().toISOString())}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ padding:'0 0 24px', borderBottom:'1px solid #14111018' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <svg width={20} height={12} viewBox="0 0 27 16" fill="none">
              <rect x="1" y="1" width="12" height="6" rx="1.2" fill={T.brick}/>
              <rect x="1" y="9" width="12" height="6" rx="1.2" fill={T.brick}/>
              <rect x="15" y="9" width="11" height="6" rx="1.2" fill={T.brick}/>
            </svg>
            <span style={{ fontSize:15, fontWeight:500, color:'#14110E', letterSpacing:-0.3, textTransform:'lowercase' }}>bricks</span>
          </div>
          <div style={{ fontSize: 12, color:'#A39684' }}>E-signature request</div>
          <div style={{ fontSize:24, fontWeight:500, letterSpacing:-0.8, color:'#14110E', marginTop:8 }}>{envelope?.title}</div>
          {envelope?.sender_name && (
            <div style={{ fontSize:13, color:'#6B6054', marginTop:6 }}>Sent by {envelope.sender_name}{envelope.sender_organization ? ` · ${envelope.sender_organization}` : ''}</div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding:'24px 0' }}>
          {/* Recipient info */}
          <div style={{ padding:'0 0 20px', borderBottom:'1px solid #14111018', marginBottom:20 }}>
            <div style={{ fontSize: 12, color:'#A39684' }}>Signing as</div>
            <div style={{ fontSize:15, fontWeight:500, color:'#14110E', marginTop:4 }}>{envelope?.recipient_name}</div>
            <div style={{ fontSize:13, color:'#6B6054' }}>{envelope?.recipient_email} · {envelope?.recipient_role || 'Signer'}</div>
          </div>

          {/* Message */}
          {envelope?.message && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize: 12, color:'#A39684', marginBottom:8 }}>Message from sender</div>
              <div style={{ fontSize:14, color:'#3B332A', lineHeight:1.6, padding:'2px 0 2px 14px', borderLeft:'2px solid #14111020' }}>{envelope.message}</div>
            </div>
          )}

          {/* Document */}
          {onDoc && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize: 12, color:'#A39684', marginBottom:8 }}>
                Document · {placedFields.length} field{placedFields.length === 1 ? '' : 's'} for you
              </div>
              <div ref={docRef} style={{ maxHeight:640, overflowY:'auto', background:'#E4DED2', padding:10, borderRadius:2, border:'1px solid #14111018' }}>
                {!pdf && <div style={{ display:'flex', justifyContent:'center', padding:40 }}><BricksLoader variant="assemble" size={32} color={T.brick} /></div>}
                {pdf && (
                  <PdfPages pdf={pdf} width={Math.max(240, docWidth - 20)}
                    renderOverlay={(pageNo, size) => placedFields.filter(f => f.page === pageNo).map(f => {
                      const box = { position:'absolute', left:f.x * size.w, top:f.y * size.h, width:f.w * size.w, height:f.h * size.h, boxSizing:'border-box', borderRadius:2 };
                      const fs = Math.max(9, Math.min(22, f.h * size.h * 0.62));
                      if (f.type === 'signature' || f.type === 'initial') {
                        const shown = f.type === 'initial' ? initialsOf(typedSignature) : typedSignature.trim();
                        return (
                          <button key={f.id} data-field type="button" onClick={() => sigInputRef.current?.focus()} title={f.label}
                            style={{ ...box, border:`1.5px ${shown ? 'solid' : 'dashed'} ${T.brick}`, background: shown ? '#FBEFE8CC' : '#FBEFE8', cursor:'pointer', padding:'0 4px', textAlign:'left', overflow:'hidden' }}>
                            <span style={{ fontFamily: shown ? 'cursive' : F.display, fontSize: shown ? fs : Math.min(fs, 11), color: shown ? '#17214F' : T.brickDeep, whiteSpace:'nowrap' }}>
                              {shown || (f.type === 'initial' ? 'Initials' : 'Sign here')}
                            </span>
                          </button>
                        );
                      }
                      if (f.type === 'date') {
                        return <div key={f.id} data-field style={{ ...box, border:`1px solid ${T.brick}66`, background:'#FBFAF8CC', display:'flex', alignItems:'center', padding:'0 4px', fontSize:Math.min(fs, 13), color:'#14110E' }}>{fieldValues[f.id] || today}</div>;
                      }
                      return (
                        <input key={f.id} data-field aria-label={f.label} placeholder={f.label} value={fieldValues[f.id] || ''}
                          onChange={e => setFieldValues(v => ({ ...v, [f.id]: e.target.value }))}
                          style={{ ...box, border:`1.5px ${String(fieldValues[f.id] || '').trim() ? 'solid' : 'dashed'} ${T.brick}`, background:'#FFFFFFE6', padding:'0 4px', fontSize:Math.min(fs, 14), fontFamily:F.display, color:'#14110E', outline:'none' }} />
                      );
                    })}
                  />
                )}
              </div>
            </div>
          )}
          {envelope?.document_url && !onDoc && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize: 12, color:'#A39684', marginBottom:8 }}>Document</div>
              <object data={envelope.document_url} type="application/pdf" style={{ width:'100%', height:480, border:'1px solid #14111018', borderRadius:2 }}>
                <div style={{ padding:16, fontSize:13, color:'#6B6054' }}>
                  Your browser can't preview this PDF inline. <a href={envelope.document_url} target="_blank" rel="noreferrer" style={{ color:T.brick }}>Open the document →</a>
                </div>
              </object>
            </div>
          )}

          {/* Fields to sign */}
          {listFields.length > 0 && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize: 12, color:'#A39684', marginBottom:12 }}>To complete · <span style={{ fontFamily:F.mono }}>{listFields.length}</span></div>
              <div style={{ display:'flex', flexDirection:'column', borderTop:'1px solid #14111018' }}>
                {listFields.map((field) => (
                  <div key={field.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 0', borderBottom:'1px solid #14111018' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:'#14110E', marginBottom:6 }}>{field.label || (field.type.charAt(0).toUpperCase() + field.type.slice(1))}
                        {field.required && <span style={{ fontSize:12, fontWeight:400, color:'#A39684', marginLeft:8 }}>Required</span>}
                      </div>
                      {(field.type === 'signature' || field.type === 'initial') ? (
                        <input type="text" value={typedSignature} onChange={e => setTypedSignature(e.target.value)}
                          placeholder="Type your full name"
                          style={{ width:'100%', padding:'8px 10px', border:'1px solid #14111018', borderRadius:2, fontSize:14, fontFamily:'cursive', color:'#14110E', background:'#FBFAF8', outline:'none', boxSizing:'border-box' }}/>
                      ) : field.type === 'date' ? (
                        <input type="text" value={fieldValues[field.id] || today} readOnly
                          style={{ width:'100%', padding:'8px 10px', border:'1px solid #14111018', borderRadius:2, fontSize:14, fontFamily:F.display, color:'#6B6054', background:'#F4F1EB', outline:'none', boxSizing:'border-box' }}/>
                      ) : (
                        <input type="text" value={fieldValues[field.id] || ''} onChange={e => setFieldValues({ ...fieldValues, [field.id]: e.target.value })}
                          style={{ width:'100%', padding:'8px 10px', border:'1px solid #14111018', borderRadius:2, fontSize:14, fontFamily:F.display, color:'#14110E', background:'#FBFAF8', outline:'none', boxSizing:'border-box' }}/>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consent */}
          <div style={{ padding:'4px 0', marginBottom:24 }}>
            <label style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer' }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                style={{ marginTop:2, width:16, height:16, accentColor:T.brick, flexShrink:0, cursor:'pointer' }}/>
              <span style={{ fontSize:13, color:'#3B332A', lineHeight:1.5 }}>
                I understand that by clicking <strong>Sign document</strong>, I am providing my electronic signature with the same legal effect as a handwritten signature. I have reviewed the document and agree to sign electronically.
              </span>
            </label>
          </div>

          {/* Typed signature, if the document has no dedicated signature field to fill it via */}
          {!listFields.some(f => f.type === 'signature' || f.type === 'initial') && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize: 12, color:'#A39684', marginBottom:8 }}>
                {placedFields.some(f => f.type === 'signature' || f.type === 'initial') ? 'Type your signature. It fills every signature and initials box.' : 'Type your signature'}
              </div>
              <input ref={sigInputRef} type="text" value={typedSignature} onChange={e => setTypedSignature(e.target.value)} placeholder="Full name"
                style={{ width:'100%', padding:'10px 12px', border:'1px solid #14111018', borderRadius:2, fontSize:16, fontFamily:'cursive', color:'#14110E', background:'#FBFAF8', outline:'none', boxSizing:'border-box' }}/>
            </div>
          )}

          {/* Sign button */}
          <button
            onClick={handleSign}
            disabled={!agreed || !typedSignature.trim() || signing || blankRequired.length > 0}
            style={{
              width:'100%', padding:'15px var(--bx-pad)', borderRadius:2, border:'none',
              background: (agreed && typedSignature.trim() && !blankRequired.length) ? '#14110E' : '#D6CFC2',
              color: (agreed && typedSignature.trim() && !blankRequired.length) ? '#F4F1EB' : '#8F8678',
              fontSize:15, fontWeight:500, fontFamily:F.display, letterSpacing:-0.2,
              cursor: (agreed && typedSignature.trim() && !blankRequired.length) ? 'pointer' : 'not-allowed',
              transition:'background 0.15s',
            }}>
            {signing ? 'Signing…' : 'Sign document'}
          </button>
          {blankRequired.length > 0 && (
            <div style={{ textAlign:'center', marginTop:8, fontSize:12, color:'#6B6054' }}>
              Fill in {blankRequired.map(f => f.label || 'Text').join(', ')} to sign.
            </div>
          )}

          {!declining ? (
            <div style={{ textAlign:'center', marginTop:14 }}>
              <button onClick={() => setDeclining(true)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B6054', fontFamily:F.display, textDecoration:'underline', textUnderlineOffset:3 }}>
                Decline to sign
              </button>
            </div>
          ) : (
            <div style={{ marginTop:16, padding:'16px 0 0', borderTop:'1px solid #14111018' }}>
              <div style={{ fontSize:13, color:'#3B332A', lineHeight:1.5, marginBottom:10 }}>
                Declining closes this request for everyone. You can tell the sender why (optional).
              </div>
              <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3} maxLength={500}
                placeholder="Reason (optional)"
                style={{ width:'100%', padding:'8px 10px', border:'1px solid #14111018', borderRadius:2, fontSize:14, fontFamily:F.display, color:'#14110E', background:'#fff', outline:'none', resize:'vertical', boxSizing:'border-box' }}/>
              <div style={{ display:'flex', gap:10, marginTop:10, justifyContent:'flex-end' }}>
                <button onClick={() => setDeclining(false)} disabled={busyDecline}
                  style={{ padding:'9px 14px', borderRadius:2, border:'1px solid #14111030', background:'transparent', color:'#3B332A', fontSize:13, fontFamily:F.display, cursor:'pointer' }}>Keep reviewing</button>
                <button onClick={handleDecline} disabled={busyDecline}
                  style={{ padding:'9px 14px', borderRadius:2, border:'none', background:'#3B332A', color:'#fff', fontSize:13, fontFamily:F.display, cursor:'pointer' }}>
                  {busyDecline ? 'Declining…' : 'Decline'}
                </button>
              </div>
            </div>
          )}

          {envelope?.expires_at && (
            <div style={{ textAlign:'center', marginTop:14, fontSize:12, color:'#6B6054' }}>
              Sign by {fmtDate(envelope.expires_at)}.
            </div>
          )}

          <div style={{ textAlign:'center', marginTop:16, fontSize:12, color:'#A39684' }}>
            Secured by Bricks · {fmtDate(new Date().toISOString())}
          </div>
        </div>
      </div>
    </div>
  );
}
